import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import {
  CreateClientAbsenceOptions,
  createClientAbsenceRequest,
  deleteClientAbsencePeriod,
  DeleteClientAbsencePeriodOptions,
  endClientAbsencePeriod,
  EndClientAbsencePeriodOptions,
  updateClientAbsence,
  UpdateClientAbsenceOptions
} from 'RestAPIs/absence';
import {
  connectAppointmentGrouping,
  ConnectAppointmentGroupingPayload,
  connectSchedule,
  ConnectSchedulePayload,
  createAppointment,
  CreateAppointmentPayload,
  endAppointments,
  EndAppointmentsPayload,
  updateRouteStart,
  UpdateRouteStartPayload
} from 'RestAPIs/activities';

import { showSnackbar } from 'components/shared';

export const useConnectAppointmentGrouping = ({
  onSuccess
}: {
  onSuccess?: (data) => void;
}) => {
  const { t } = useTranslation();

  return useMutation(
    (payload: ConnectAppointmentGroupingPayload) =>
      connectAppointmentGrouping(payload),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      },
      onSuccess
    }
  );
};

export const useConnectSchedule = ({
  onSuccess
}: {
  onSuccess?: (data) => void;
}) => {
  const { t } = useTranslation();

  return useMutation(
    (payload: ConnectSchedulePayload) => connectSchedule(payload),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      },
      onSuccess
    }
  );
};

export const useUpdateRouteStart = () => {
  const { t } = useTranslation();

  return useMutation(
    ({
      appointmentGroupingStart,
      appointmentGroupingId,
      editAppointmentType,
      suppressWarnings,
      start
    }: UpdateRouteStartPayload) =>
      updateRouteStart({
        originalAppointmentGroupingStart: appointmentGroupingStart,
        appointmentGroupingId,
        editAppointmentType,
        suppressWarnings,
        start
      }),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

export const useEndAppointments = () => {
  const { t } = useTranslation();

  return useMutation(
    ({ date, appointmentIds }: EndAppointmentsPayload) =>
      endAppointments({
        date,
        appointmentIds
      }),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

export const useCreateAppointment = () => {
  const { t } = useTranslation('common');
  return useMutation(
    async ({
      start,
      durationInSeconds,
      activityEntityId,
      organizationalUnitId,
      description,
      recurrenceRule
    }: CreateAppointmentPayload) =>
      await createAppointment({
        start,
        durationInSeconds,
        activityEntityId,
        organizationalUnitId,
        description,
        recurrenceRule
      }),
    {
      onSuccess: () => {
        showSnackbar({ severity: 'success', message: t('changesSaved') });
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

const CLIENT_ABSENCES_QUERY_KEY = 'client-absences';

export const useCreateAbsence = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateClientAbsenceOptions) => {
      await createClientAbsenceRequest(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useUpdateClientAbsence = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateClientAbsenceOptions) => {
      await updateClientAbsence(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useEndClientAbsencePeriod = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: EndClientAbsencePeriodOptions) => {
      await endClientAbsencePeriod(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);
      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useDeleteClientAbsencePeriod = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteClientAbsencePeriodOptions) => {
      await deleteClientAbsencePeriod(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};
